'use client';

import React, {
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CloseIcon } from '@heroui/shared-icons';
import { NextUI } from '@/components/NextUI';
import useVerificationCodeLogin, {
  VerificationFlowError,
} from '@/features/auth/hooks/useVerificationCodeLogin';
import { trackVerificationCodeEvent } from '@/features/auth/analytics/verificationCodeAnalytics';
import { User } from '@/types/models/user';
import { ChevronLeft } from 'lucide-react';

const CODE_LENGTH = 6;
const DEFAULT_RESEND_DELAY_SECONDS = 60;
const SUCCESS_DISPLAY_DURATION_MS = 1000;
type VerificationCodeMode = 'verification' | 'login';

const maskEmail = (email: string) => {
  const [localPart, domain] = email.split('@');
  if (!domain) return email;

  return `${localPart.slice(0, Math.min(3, localPart.length))}***@${domain}`;
};

interface VerificationCodeModalProps {
  email: string;
  initialResendDelay?: number;
  verificationMode?: VerificationCodeMode;
  onClose: () => void;
  onUsePassword: () => void;
  onVerified: (user: User) => void | Promise<void>;
}

const VerificationCodeModal = ({
  email,
  initialResendDelay = DEFAULT_RESEND_DELAY_SECONDS,
  verificationMode = 'verification',
  onClose,
  onUsePassword,
  onVerified,
}: VerificationCodeModalProps) => {
  const { resendCode, sendLoginCode, verifyAndLogin } =
    useVerificationCodeLogin();
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(initialResendDelay);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const hasTrackedScreenView = useRef(false);

  const code = digits.join('');

  useEffect(() => {
    if (!hasTrackedScreenView.current) {
      trackVerificationCodeEvent('verification_code_screen_viewed');
      hasTrackedScreenView.current = true;
    }
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;

    const timeoutId = window.setTimeout(
      () => setCountdown((current) => Math.max(0, current - 1)),
      1000
    );

    return () => window.clearTimeout(timeoutId);
  }, [countdown]);

  const updateDigit = (index: number, value: string) => {
    const numericValue = value.replace(/\D/g, '');

    if (numericValue.length > 1) {
      const autofillDigits = numericValue.slice(0, CODE_LENGTH).split('');
      setDigits(
        Array.from(
          { length: CODE_LENGTH },
          (_, digitIndex) => autofillDigits[digitIndex] ?? ''
        )
      );
      setError('');
      setStatus('');
      inputRefs.current[
        Math.min(autofillDigits.length, CODE_LENGTH) - 1
      ]?.focus();
      return;
    }

    const nextValue = numericValue.slice(-1);
    const nextDigits = [...digits];
    nextDigits[index] = nextValue;
    setDigits(nextDigits);
    setError('');
    setStatus('');

    if (nextValue && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    event: KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const pastedDigits = event.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, CODE_LENGTH)
      .split('');

    if (!pastedDigits.length) return;

    event.preventDefault();
    setDigits(
      Array.from(
        { length: CODE_LENGTH },
        (_, index) => pastedDigits[index] ?? ''
      )
    );
    setError('');
    setStatus('');
    inputRefs.current[Math.min(pastedDigits.length, CODE_LENGTH) - 1]?.focus();
  };

  const handleSubmit = async () => {
    if (code.length !== CODE_LENGTH || isSubmitting) return;

    trackVerificationCodeEvent('verification_code_submitted');
    setIsSubmitting(true);
    setIsSuccess(false);
    setError('');
    setStatus('');

    try {
      const user = await verifyAndLogin(email, code);
      setIsSuccess(true);
      setStatus('Success');
      await new Promise((resolve) =>
        window.setTimeout(resolve, SUCCESS_DISPLAY_DURATION_MS)
      );
      await onVerified(user);
    } catch (verificationError) {
      const normalizedError =
        verificationError instanceof VerificationFlowError
          ? verificationError
          : new VerificationFlowError();

      trackVerificationCodeEvent('verification_code_failed', {
        reason: normalizedError.reason ?? 'UNKNOWN',
      });
      setError(normalizedError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0 || isResending || isSubmitting) return;

    trackVerificationCodeEvent('verification_code_resent');
    setIsResending(true);
    setIsSuccess(false);
    setError('');
    setStatus('');

    try {
      const response =
        verificationMode === 'login'
          ? await sendLoginCode(email)
          : await resendCode(email);
      setDigits(Array(CODE_LENGTH).fill(''));
      setCountdown(response.retryAfterSeconds ?? DEFAULT_RESEND_DELAY_SECONDS);
      setStatus('A new code has been sent.');
      inputRefs.current[0]?.focus();
    } catch (resendError) {
      const normalizedError =
        resendError instanceof VerificationFlowError
          ? resendError
          : new VerificationFlowError();

      if (normalizedError.retryAfterSeconds) {
        setCountdown(normalizedError.retryAfterSeconds);
      }
      setError(normalizedError.message);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(124,124,124,0.5)] px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="verification-code-title"
    >
      <form
        className="relative flex w-full max-w-[419px] flex-col items-center gap-4 rounded-[10px] bg-white p-8 max-sm:px-5"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        {!isSuccess && (
          <button
            type="button"
            aria-label="Back to password login"
            className="absolute left-[11px] top-3 flex size-6 items-center justify-center text-gray-medium transition-colors hover:text-gray-dark disabled:pointer-events-none disabled:opacity-50"
            onClick={onUsePassword}
            disabled={isSubmitting}
          >
            <ChevronLeft size={16} strokeWidth={1.5} />
          </button>
        )}

        <button
          type="button"
          aria-label="Close verification code dialog"
          className="absolute right-2 top-3 flex size-6 items-center justify-center text-gray-medium transition-colors hover:text-gray-dark disabled:pointer-events-none disabled:opacity-50"
          onClick={onClose}
          disabled={isSubmitting}
        >
          <CloseIcon className="size-4" />
        </button>

        <div className="flex w-full flex-col items-center gap-2 text-center">
          <h2
            id="verification-code-title"
            className="w-full font-barlow text-[20px] font-semibold leading-6 text-gray-dark"
          >
            Check your email
          </h2>
          <p className="w-[300px] max-w-full font-barlow text-[15px] font-medium leading-[18px] text-black/50">
            Enter the 6-digit code we sent to {maskEmail(email)} to log in.
          </p>
        </div>

        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex w-full flex-col items-start gap-2">
            <div
              className="flex w-full gap-1"
              onPaste={handlePaste}
              aria-label="Six digit verification code"
            >
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(element) => {
                    inputRefs.current[index] = element;
                  }}
                  value={digit}
                  onChange={(event) => updateDigit(index, event.target.value)}
                  onKeyDown={(event) => handleKeyDown(index, event)}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  autoComplete={index === 0 ? 'one-time-code' : 'off'}
                  aria-label={`Verification code digit ${index + 1}`}
                  disabled={isSubmitting}
                  className={`h-[53px] min-w-0 flex-1 rounded-[5px] border bg-white text-center font-barlow text-[20px] font-medium text-black outline-none transition-colors focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/15 disabled:bg-gray-50 ${
                    isSuccess
                      ? 'border-[#17b26a]'
                      : error
                        ? 'border-[#ec4130]'
                        : 'border-gray-300'
                  }`}
                />
              ))}
            </div>

            {(error || status) && (
              <div className="w-full text-center font-barlow text-[14px] font-medium leading-[17px]">
                {error ? (
                  <p className="text-[#ec4130]" role="alert">
                    {error}
                  </p>
                ) : (
                  <p
                    className={isSuccess ? 'text-[#34a853]' : 'text-green-600'}
                    role="status"
                  >
                    {status}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex w-full flex-col items-start gap-3">
            <p className="w-full text-center font-barlow text-[14px] font-medium leading-[17px] text-black/50">
              Didn&apos;t get the code?{' '}
              <button
                type="button"
                className="font-medium text-gray-dark enabled:hover:text-primary-blue disabled:cursor-default"
                onClick={handleResend}
                disabled={countdown > 0 || isResending || isSubmitting}
              >
                {isResending
                  ? 'Sending...'
                  : countdown > 0
                    ? `Resend in ${countdown}s`
                    : 'Resend'}
              </button>
            </p>

            <div className="flex w-full flex-col items-start gap-2">
              <NextUI.Button
                type="submit"
                className="w-full !font-barlow"
                isDisabled={code.length !== CODE_LENGTH || isSubmitting}
                isLoading={isSubmitting}
              >
                {isSubmitting ? '' : 'Confirm'}
              </NextUI.Button>

              <div className="flex h-[11px] w-full items-center gap-2 font-barlow text-[14px] font-medium text-gray-dark">
                <span className="h-px flex-1 bg-gray-medium2" />
                <span className="w-[17px] text-center leading-[11px]">or</span>
                <span className="h-px flex-1 bg-gray-medium2" />
              </div>

              <NextUI.Button
                type="button"
                variant="link"
                className="flex h-12 w-full !font-barlow !text-[17px]"
                isDisabled={isSubmitting}
                onPress={onUsePassword}
              >
                Log in with password
              </NextUI.Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default VerificationCodeModal;
